import { ReactNode, useCallback, useReducer } from 'react';

import type { SavedQuery } from '@/sections/insights/queryBuilder/types/SavedQuery';
import { unflatten } from '@/sections/insights/queryBuilder/utils/flatGraphConversion';
import { validateQuery } from '@/sections/insights/queryBuilder/utils/validateQuery';

import { QueryDataContext, QueryDataContextValue } from './QueryDataContext';
import { queryStateReducer } from './queryStateReducer';

/**
 * Provider component for query data context
 *
 * Manages queryState with reducer for all graph mutations.
 * Provides dispatch for actions and getSavedQuery for conversions.
 */
export function QueryDataProvider({ children }: { children: ReactNode }) {
  const [queryState, dispatch] = useReducer(queryStateReducer, null);

  const getSavedQuery = useCallback((): SavedQuery | null => {
    if (!queryState) return null;

    // Validate graph structure before unflatten to prevent infinite loop crashes
    const validationResult = validateQuery(queryState.flatGraph);
    if (!validationResult.isValid) {
      console.error(
        'Cannot convert invalid query to SavedQuery:',
        validationResult.errors[0]
      );
      return null;
    }

    const baseQuery = unflatten(queryState.flatGraph);

    return {
      id: queryState.metadata.id,
      name: queryState.metadata.name,
      description: queryState.metadata.description,
      folderId: queryState.metadata.folderId,
      type: queryState.metadata.type,
      query: {
        ...baseQuery,
        limit: queryState.metadata.limit,
        orderBy: queryState.metadata.orderBy,
        ...(queryState.metadata.descending !== undefined && {
          descending: queryState.metadata.descending,
        }),
        shortest: queryState.metadata.shortest,
      },
    };
  }, [queryState]);

  const value: QueryDataContextValue = {
    queryState,
    dispatch,
    getSavedQuery,
  };

  return (
    <QueryDataContext.Provider value={value}>
      {children}
    </QueryDataContext.Provider>
  );
}
